import type { User, VisitType } from '@lmc/ts-common'
import {
  FormCard,
  FormItem,
  GroupedUsersMultiSelect,
  Input,
  MultiSelect,
  SelectAddOption
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { FormInstance } from 'antd'
import { InputNumber, Switch } from 'antd'
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import type { InitialState } from 'screens/Rostering/components/ModalStepperForm'
import { useInvoicingChargeRateExists } from 'screens/Rostering/hooks/useInvoicingChargeRateExists'
import { useGetInvoicingSettings } from 'screens/Rostering/hooks/useInvoicingRates'
import type { RotaInstanceStatus } from 'screens/Rostering/types'
import {
  getRequiredRule,
  getTeamMembersClockInsRule
} from 'screens/Rostering/utils'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import { cleanDecimalValues } from '../../formComponents/utils'

type InstanceTypeValue = {
  id: string
  name: string
}

type InstanceFormCardProps = {
  testId?: string
  form: FormInstance
  formData: InitialState
  visitTypes?: VisitType[]
  users?: User[]
  allSkills?: Array<{ id: string; name: string }>
  flagsRosteringEditingLimitations?: boolean
  flagRotaInvoicing?: boolean
  isAdmin?: boolean
  setFormData: Dispatch<SetStateAction<InitialState>>
  isSubmitting?: boolean
}

const InstanceFormCard: FC<InstanceFormCardProps> = ({
  testId,
  form,
  formData,
  visitTypes,
  users,
  allSkills,
  flagsRosteringEditingLimitations,
  flagRotaInvoicing,
  isAdmin,
  setFormData,
  isSubmitting = false
}) => {
  const { t } = useTranslation()
  const baseId = testId || 'instance-form-card'
  const [showOptionalDetails, setShowOptionalDetails] = useState(false)
  const [instanceTypeValue, setInstanceTypeValue] = useState<
    InstanceTypeValue | undefined
  >(undefined)
  const [showValidation, setShowValidation] = useState(false)

  const { data: invoicingSettings } = useGetInvoicingSettings()
  const isSiteInvoicingEnabled =
    !!flagRotaInvoicing && (invoicingSettings?.enabled ?? false)

  const {
    data: chargeRateData,
    isLoading: isChargeRateLoading,
    isIdle: isChargeRateIdle
  } = useInvoicingChargeRateExists({
    subjectId: formData?.subjectId as string | undefined,
    fundingTypeId: formData?.fundingTypeId as string | undefined,
    eventTypeId: instanceTypeValue?.id,
    enabled: isSiteInvoicingEnabled && !!isAdmin
  })

  const instanceTypeOptions =
    visitTypes?.map((item) => ({
      value: {
        // @ts-ignore
        id: item._id || item.id,
        name: item.name
      },
      label: item.name
    })) || []

  const onSkillsChange = (selectedSkills: string[]) => {
    const formattedSkills = selectedSkills.map((skillId) => ({
      skillId,
      skillSupplyNumber: 1
    }))

    form.setFieldValue('skills', formattedSkills)
    setFormData((prev) => ({
      ...prev,
      skills: formattedSkills
    }))
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (formData?.instanceTypeId && !instanceTypeValue) {
      const foundType = instanceTypeOptions.find(
        (option) => option.value.id === formData.instanceTypeId
      )
      if (foundType) {
        setInstanceTypeValue(foundType.value)
      }
    }
  }, [formData?.instanceTypeId, instanceTypeOptions, instanceTypeValue])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isSubmitting) {
      setShowValidation(true)
    }
  }, [isSubmitting])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (instanceTypeValue) {
      setShowValidation(false)
    }
  }, [instanceTypeValue])

  const handleInstanceTypeChange = (value: InstanceTypeValue) => {
    setInstanceTypeValue(value)
    const isNewInstanceType = !instanceTypeOptions.some(
      (option) =>
        option.value.id === value.id && option.value.name === value.name
    )

    if (isNewInstanceType) {
      setFormData((prev) => ({
        ...prev,
        newInstanceType: {
          name: value.name
        },
        instanceTypeId: undefined
      }))
    } else if (value && value.id) {
      setFormData((prev) => ({
        ...prev,
        instanceTypeId: value.id,
        newInstanceType: undefined
      }))

      form?.setFieldValue('instanceTypeId', value.id)
    } else {
      setFormData((prev) => ({
        ...prev,
        instanceTypeId: undefined,
        newInstanceType: undefined
      }))
    }
  }

  const handleOptionalDetailsToggle = (checked: boolean) => {
    setShowOptionalDetails(checked)

    if (!checked) {
      form.setFieldsValue({
        skills: undefined,
        slotNotes: undefined
      })

      setFormData((prev) => ({
        ...prev,
        skills: undefined,
        slotNotes: undefined
      }))
    }
  }

  return (
    <FormCard level={2} padding="16px" data-testid={baseId}>
      <FormItem
        data-testid={commonPatterns.field(baseId, 'visit-type')}
        label={
          <MandatoryText>
            <RedText>*</RedText>
            <Text>{t('TYPE_OF_VISIT')}</Text>
          </MandatoryText>
        }
        rules={[getRequiredRule(t('TYPE_OF_VISIT'))]}
        validateStatus={showValidation && !instanceTypeValue ? 'error' : ''}
        help={
          showValidation && !instanceTypeValue
            ? t('TYPE_OF_VISIT') + ' is required'
            : ''
        }
      >
        <SelectAddOption<InstanceTypeValue>
          data-testid={commonPatterns.input(baseId, 'visit-type')}
          options={instanceTypeOptions}
          selectedValue={instanceTypeValue}
          setSelectedValue={handleInstanceTypeChange}
          valueKey="id"
          fields={[
            {
              key: 'name',
              placeholder: t('TYPE_NEW_VISIT_TYPE'),
              type: 'text',
              size: 100
            }
          ]}
        />
      </FormItem>

      {isSiteInvoicingEnabled &&
        isAdmin &&
        instanceTypeValue &&
        !isChargeRateLoading &&
        !isChargeRateIdle && (
          <ChargeRateText data-testid="add-rota-pattern-dropdown-modal-visit-instance-card-charge-rate-message">
            {chargeRateData?.exists
              ? t('CHARGE_RATE', {
                  rate: chargeRateData.rate,
                  unit: chargeRateData.rateType === 'HOURLY' ? 'Hour' : 'Event'
                })
              : t('NO_CHARGE_RATE_FOR_VISIT_TYPE')}
          </ChargeRateText>
        )}

      <FormItem name="instanceTypeId" hidden>
        <input type="hidden" />
      </FormItem>

      <FormItem
        data-testid={commonPatterns.field(baseId, 'title')}
        name="title"
        label={<Text>{t('TITLE')}</Text>}
        rules={[getRequiredRule(t('TITLE'))]}
      >
        <Input data-testid={commonPatterns.input(baseId, 'title')} />
      </FormItem>

      <InlineFormItem data-testid={`${baseId}-team-members-section`}>
        <NumberSupplyFormItem
          data-testid={commonPatterns.field(baseId, 'number-supply')}
          labelCol={labelColStyle}
          labelAlign="left"
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
          rules={[
            getRequiredRule(t('TEAM_MEMBERS_REQUIRED')),
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    ...getTeamMembersClockInsRule(
                      formData?.status as RotaInstanceStatus,
                      formData?.clockIns as unknown[] | undefined
                    ),
                    validateTrigger: 'onSubmit'
                  }
                ]
              : [])
          ]}
        >
          <InputNumber
            data-testid={commonPatterns.input(baseId, 'number-supply')}
            style={{ width: 72 }}
            min={1}
            max={50}
            type="number"
            formatter={cleanDecimalValues}
          />
        </NumberSupplyFormItem>
        <TeamMembersFormItem
          data-testid={commonPatterns.field(baseId, 'team-members')}
          name="teamMembers"
        >
          <GroupedUsersMultiSelect
            data-testid={commonPatterns.input(baseId, 'team-members')}
            users={(users ?? []) as unknown as User[]}
            title={t('SELECT_TEAM_MEMBERS')}
            values={(formData?.teamMembers as string[] | undefined) || []}
            hasClear
            hasSearch
            hasSelectAll
            placeholder
            maxTagCount={0}
            suffixIcon={
              <Text>
                {(formData?.teamMembers as string[] | undefined)?.length ?? 0}/
                {Number(formData?.numberSupply ?? 0)}
              </Text>
            }
          />
        </TeamMembersFormItem>
      </InlineFormItem>
      <FormItem data-testid={`${baseId}-optional-toggle`}>
        <FlexContainer>
          <Switch
            data-testid={commonPatterns.button(baseId, 'optional-toggle')}
            checked={showOptionalDetails}
            onChange={handleOptionalDetailsToggle}
          />
          <StyledText>{t('OPTIONAL_DETAILS')}</StyledText>
        </FlexContainer>
      </FormItem>
      {showOptionalDetails && (
        <>
          <FormItem
            data-testid={commonPatterns.field(baseId, 'skills')}
            name="skills"
            label={<Text>{t('SKILLS_REQUIRED')}</Text>}
          >
            <MultiSelect
              data-testid={commonPatterns.input(baseId, 'skills')}
              title={t('SELECT_SKILLS_SHORT')}
              options={allSkills?.map((skill) => ({
                value: skill?.id,
                label: skill?.name
              }))}
              hasClear
              hasSearch
              hasSelectAll
              values={
                (
                  formData?.skills as
                    | Array<string | { skillId: string }>
                    | undefined
                )?.map((skill) =>
                  typeof skill === 'string' ? skill : skill.skillId
                ) || []
              }
              onChange={onSkillsChange}
            />
          </FormItem>
          <FormItem
            data-testid={commonPatterns.field(baseId, 'description')}
            name="slotNotes"
            label={<Text>{t('DESCRIPTION')}</Text>}
          >
            <Input data-testid={commonPatterns.input(baseId, 'description')} />
          </FormItem>
        </>
      )}
    </FormCard>
  )
}

const labelColStyle = {
  style: {
    width: 200
  }
}

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NumberSupplyFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color4};
  }
  width: 200px;
`

const TeamMembersFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  flex: 1;
  min-width: 0;
`

const InlineFormItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const MandatoryText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const RedText = styled.span`
  color: red;
`

const ChargeRateText = styled(Text)`
  margin-top: -8px;
`

export default InstanceFormCard
